import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Typography from "../../../../components/Typography/Typography";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import { Button } from "../../../../components/Buttons";
import IconComponent from "../../../../components/Icon/IconComponent";
import { postRequest } from "../../../../Apis/apiWrapper";
import { paymentEndpoints } from "../../../../utils/constants/httpConstants";
import {
  BRAND,
  GREY,
  BASE,
  SUCCESS,
  BORDER,
} from "../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../utils/constants/UI";
import { EMITTER_ERROR } from "../../../../utils/constants";
import EventEmitter from "../../../../utils/emitter/EventEmitter";
import {
  PROFILE_PATH,
  INITIAL_ROUTE,
} from "../../../../utils/constants/routes";
import { checkLogin } from "../../../../redux/actions/authActions";

const BUY_PACKAGE_TAB_PATH = `${PROFILE_PATH}?tab=Billing`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState(false);

  const userDetailsSelector = useSelector((state) => state?.userDetails);

  const styles = {
    container: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100vh",
      background: BASE.BACKGROUND,
      padding: "24px",
    },
    card: {
      background: BASE.WHITE,
      borderRadius: "12px",
      padding: "48px",
      maxWidth: "500px",
      width: "100%",
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "20px",
      border: `1px solid ${BORDER.DEFAULT}`,
    },
    iconWrapper: {
      width: "80px",
      height: "80px",
      borderRadius: "50%",
      background: SUCCESS.W_50,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    successIcon: {
      fontSize: "40px",
      color: SUCCESS.W_500,
    },
    errorIconWrapper: {
      width: "80px",
      height: "80px",
      borderRadius: "50%",
      background: "#FEF2F2",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    errorIcon: {
      fontSize: "40px",
      color: BRAND.PRIMARY,
    },
    detailsBox: {
      background: BASE.BACKGROUND,
      borderRadius: "8px",
      padding: "16px",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    detailRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    buttonGroup: {
      display: "flex",
      gap: "12px",
      width: "100%",
      marginTop: "8px",
    },
  };

  // Determine where to go based on current user state
  const payload = userDetailsSelector?.payload;
  const setupComplete =
    payload?.account_setting_setup && payload?.general_setting_setup;

  useEffect(() => {
    if (!verifying && !error && payload) {
      console.log("[PaymentSuccess] Checking for onboarding redirect:", {
        setupComplete,
        payload,
      });
      if (!setupComplete) {
        console.log(
          "[PaymentSuccess] Setup incomplete, redirecting to Onboarding Step 1",
        );
        // Use state to force the step override in InitialForm
        navigate(INITIAL_ROUTE, { state: { step: 1 } });
      } else {
        console.log(
          "[PaymentSuccess] Setup complete, staying on success page.",
        );
      }
    }
  }, [verifying, error, payload, setupComplete, navigate]);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    console.log("[PaymentSuccess] Session ID from URL:", sessionId);
    if (sessionId) {
      verifyPayment(sessionId);
    } else {
      console.warn("[PaymentSuccess] No session ID found in URL.");
      setVerifying(false);
      setError(true);
    }
  }, []);

  const verifyPayment = async (sessionId) => {
    try {
      console.log(
        "[PaymentSuccess] Starting verification for session:",
        sessionId,
      );
      setVerifying(true);
      const response = await postRequest(
        navigate,
        paymentEndpoints.verifySession,
        { session_id: sessionId },
      );

      console.log("[PaymentSuccess] Verification response:", response?.data);

      if (response?.data?.status) {
        setPaymentDetails(response.data.data);
        console.log(
          "[PaymentSuccess] Verification successful, refreshing user data...",
        );
        // Refresh user data in background WITHOUT navigate to prevent auto-redirect
        // Await this to ensure Redux state is updated before any redirect logic kicks in
        await dispatch(checkLogin());
        console.log("[PaymentSuccess] User data refresh triggered.");
      } else {
        console.error("[PaymentSuccess] Verification API returned failure.");
        setError(true);
      }
    } catch (err) {
      console.error("[PaymentSuccess] Error verifying payment:", err);
      setError(true);
      EventEmitter.emit(
        EMITTER_ERROR,
        err?.data?.message || "Failed to verify payment.",
      );
    } finally {
      setVerifying(false);
    }
  };

  if (verifying) {
    return (
      <Box style={styles.container}>
        <Box style={styles.card}>
          <Typography
            size={"lg"}
            color={GREY.PRIMARY}
            fontWeight={fontWeight.SEMIBOLD}
            label="Verifying your payment..."
          />
          <TypographyInline
            size={"sm"}
            color={GREY.SECONDARY}
            fontWeight={fontWeight.REGULAR}
            label="Please wait while we confirm your payment."
          />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box style={styles.container}>
        <Box style={styles.card}>
          <Box style={styles.errorIconWrapper}>
            <IconComponent
              iconLabel={ICON_LABELS.ERROR}
              style={styles.errorIcon}
            />
          </Box>
          <Typography
            size={"lg"}
            color={GREY.PRIMARY}
            fontWeight={fontWeight.SEMIBOLD}
            label="Payment Verification Failed"
          />
          <TypographyInline
            size={"sm"}
            color={GREY.SECONDARY}
            fontWeight={fontWeight.REGULAR}
            label="We couldn't verify your payment. If you were charged, please contact support."
          />
          <Box style={styles.buttonGroup}>
            <Button
              label="Back to Onboarding"
              size={"md40"}
              fullWidth
              variant="primary"
              onClick={() => navigate(INITIAL_ROUTE)}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box style={styles.container}>
      <Box style={styles.card}>
        <Box style={styles.iconWrapper}>
          <IconComponent
            iconLabel={ICON_LABELS.CHECK_CIRCLE}
            style={styles.successIcon}
          />
        </Box>
        <Typography
          size={"lg"}
          color={GREY.PRIMARY}
          fontWeight={fontWeight.BOLD}
          label="Payment Successful!"
        />
        <TypographyInline
          size={"sm"}
          color={GREY.SECONDARY}
          fontWeight={fontWeight.REGULAR}
          label="Your subscription is now active and credits have been added to your account."
        />

        {paymentDetails && (
          <Box style={styles.detailsBox}>
            {paymentDetails.payment_status && (
              <Box style={styles.detailRow}>
                <TypographyInline
                  size={"sm"}
                  color={GREY.SECONDARY}
                  fontWeight={fontWeight.REGULAR}
                  label="Status"
                />
                <TypographyInline
                  size={"sm"}
                  color={SUCCESS.W_500}
                  fontWeight={fontWeight.SEMIBOLD}
                  label={
                    paymentDetails.payment_status === "paid"
                      ? "Paid"
                      : paymentDetails.payment_status
                  }
                />
              </Box>
            )}
            {paymentDetails.amount_total != null && (
              <Box style={styles.detailRow}>
                <TypographyInline
                  size={"sm"}
                  color={GREY.SECONDARY}
                  fontWeight={fontWeight.REGULAR}
                  label="Amount"
                />
                <TypographyInline
                  size={"sm"}
                  color={GREY.PRIMARY}
                  fontWeight={fontWeight.SEMIBOLD}
                  label={`$${(paymentDetails.amount_total / 100).toFixed(2)}`}
                />
              </Box>
            )}
            {paymentDetails.customer_email && (
              <Box style={styles.detailRow}>
                <TypographyInline
                  size={"sm"}
                  color={GREY.SECONDARY}
                  fontWeight={fontWeight.REGULAR}
                  label="Email"
                />
                <TypographyInline
                  size={"sm"}
                  color={GREY.PRIMARY}
                  fontWeight={fontWeight.MEDIUM}
                  label={paymentDetails.customer_email}
                />
              </Box>
            )}
          </Box>
        )}

        <Box style={styles.buttonGroup}>
          <Button
            label={setupComplete ? "Back to Billing" : "Back to Onboarding"}
            size={"md40"}
            fullWidth
            variant="primary"
            onClick={() =>
              navigate(setupComplete ? BUY_PACKAGE_TAB_PATH : INITIAL_ROUTE)
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PaymentSuccess;
